import React, { useState } from 'react'
import { 
  Users, 
  Plus, 
  ChevronDown, 
  ChevronRight, 
  Phone, 
  Calendar, 
  Pause, 
  X, 
  Star,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowUpDown,
  Search
} from 'lucide-react'
import { apiRequest } from '@/config/api'
import CandidateDetails from './CandidateDetails'
import CandidateSkillScore from './CandidateSkillScore'
import ApplicantTableRow from '../positions/ApplicantTableRow'

interface PositionSkill {
  skill_id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  is_good_to_have?: boolean
}

interface PositionCertification {
  certification_name: string
  certification_body?: string
  is_required: boolean
  expiry_required?: boolean
}

interface TimelineEvent {
  type: string
  name: string
  date: string | null
  notes: string | null
  status: string
  byId: number | null
  byName: string | null
  metadata: any
}

interface Candidate {
  id: number
  candidate_uuid: string
  candidate_position_uuid?: string
  first_name: string
  last_name: string
  full_name: string
  email: string
  phone?: string
  current_title?: string
  current_company?: string
  location?: string
  skills?: string[] | any
  application_status?: string
  timeline?: TimelineEvent[]
  applied_at?: string
  last_updated?: string
}

type CandidateViewType = 'shortlisted' | 'applicants' | 'onhold' | 'rejected' | 'all'

interface CandidatesTableViewProps {
  candidates: Candidate[]
  viewType?: CandidateViewType
  title?: string
  onAddCandidate?: () => void
  loading?: boolean
  positionId?: string
  positionSkills?: PositionSkill[]
  positionCertifications?: PositionCertification[]
  onCandidateUpdated?: () => void
  // Applicant-specific callbacks
  onShortlist?: (applicationId: number) => void
  onUpdateStatus?: (applicationId: number, status: string) => void
  onDownloadResume?: (applicationId: number, filename?: string) => void
  // State management for applicants
  updating?: number | null
  downloading?: number | null
}

const CandidatesTableView = ({ 
  candidates, 
  viewType = 'shortlisted',
  title,
  onAddCandidate, 
  loading, 
  positionId,
  positionSkills = [],
  positionCertifications = [],
  onCandidateUpdated,
  onShortlist,
  onUpdateStatus,
  onDownloadResume,
  updating,
  downloading
}: CandidatesTableViewProps) => {
  const [expandedCandidates, setExpandedCandidates] = useState<Set<number>>(new Set())
  const [candidateTimelines, setCandidateTimelines] = useState<{[key: number]: TimelineEvent[]}>({})
  const [loadingTimelines, setLoadingTimelines] = useState<Set<number>>(new Set())
  const [actingOnCandidate, setActingOnCandidate] = useState<{[key: number]: string}>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'score' | 'status'>('score')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Helper function to get view configuration
  const getViewConfig = () => {
    switch (viewType) {
      case 'applicants':
        return {
          title: title || 'Job Applicants',
          description: 'External applicants who applied through job posting',
          emptyTitle: 'No applicants yet',
          emptyDescription: 'No external applicants have applied for this position yet.',
          showAddButton: false,
          showShortlistActions: true
        }
      case 'onhold':
        return {
          title: title || 'On Hold Candidates',
          description: 'Candidates currently on hold in the pipeline',
          emptyTitle: 'No candidates on hold',
          emptyDescription: 'No candidates are currently on hold.',
          showAddButton: false,
          showShortlistActions: false
        }
      case 'rejected':
        return {
          title: title || 'Rejected Candidates',
          description: 'Candidates who were rejected during the process',
          emptyTitle: 'No rejected candidates',
          emptyDescription: 'No candidates have been rejected yet.',
          showAddButton: false,
          showShortlistActions: false
        }
      case 'all':
        return {
          title: title || 'All Candidates',
          description: 'All candidates in the system',
          emptyTitle: 'No candidates yet',
          emptyDescription: 'No candidates have been added yet.',
          showAddButton: !!onAddCandidate,
          showShortlistActions: false
        }
      default: // 'shortlisted'
        return {
          title: title || 'Shortlisted Candidates',
          description: 'Candidates who have been shortlisted for this position',
          emptyTitle: 'No candidates yet',
          emptyDescription: 'Add candidates to see them in the pipeline.',
          showAddButton: !!onAddCandidate,
          showShortlistActions: false
        }
    }
  }

  const viewConfig = getViewConfig()

  const toggleExpanded = async (candidateId: number) => {
    const newExpanded = new Set(expandedCandidates)
    
    if (expandedCandidates.has(candidateId)) {
      newExpanded.delete(candidateId)
      setExpandedCandidates(newExpanded)
    } else {
      newExpanded.add(candidateId)
      setExpandedCandidates(newExpanded)
      
      // Load timeline data if not already loaded
      if (!candidateTimelines[candidateId]) {
        await loadCandidateTimeline(candidateId)
      }
    }
  }

  const loadCandidateTimeline = async (candidateId: number) => {
    setLoadingTimelines(prev => new Set([...prev, candidateId]))
    
    try {
      // Use position-specific timeline endpoint with positionId parameter
      const timelineUrl = positionId 
        ? `candidates/${candidateId}/timeline?position_id=${positionId}`
        : `candidates/${candidateId}/timeline`
      
      const response = await apiRequest(timelineUrl)
      const data = await response.json()
      
      if (response.ok && data.success) {
        setCandidateTimelines(prev => ({
          ...prev,
          [candidateId]: data.data.timeline || []
        }))
      } else {
        console.error('Failed to load timeline:', data.error)
        // For now, use empty timeline
        setCandidateTimelines(prev => ({
          ...prev,
          [candidateId]: []
        }))
      }
    } catch (error) {
      console.error('Error loading timeline:', error)
      setCandidateTimelines(prev => ({
        ...prev,
        [candidateId]: []
      }))
    } finally {
      setLoadingTimelines(prev => {
        const newSet = new Set(prev)
        newSet.delete(candidateId)
        return newSet
      })
    }
  }

  const handleCandidateAction = async (candidateUuid: string, candidateId: number, action: string, notes?: string) => {
    setActingOnCandidate(prev => ({ ...prev, [candidateId]: action }))
    
    try {
      const response = await apiRequest(`candidates/position-action`, {
        method: 'POST',
        body: JSON.stringify({
          candidate_id: candidateUuid,
          position_id: positionId,
          action: action,
          notes: notes || ''
        })
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        // Refresh candidate data
        if (onCandidateUpdated) {
          onCandidateUpdated()
        }
        
        // Refresh timeline if expanded
        if (expandedCandidates.has(candidateId)) {
          await loadCandidateTimeline(candidateId)
        }
      } else {
        alert(`Failed to ${action} candidate: ${data.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error(`Error ${action} candidate:`, error)
      alert(`Failed to ${action} candidate`)
    } finally {
      setActingOnCandidate(prev => {
        const newState = { ...prev }
        delete newState[candidateId]
        return newState
      })
    }
  }


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'applied': return 'bg-blue-100 text-blue-800'
      case 'shortlisted': return 'bg-green-100 text-green-800'
      case 'screening': return 'bg-orange-100 text-orange-800'
      case 'interview1': return 'bg-purple-100 text-purple-800'
      case 'interview2': return 'bg-purple-100 text-purple-800'
      case 'interview3': return 'bg-purple-100 text-purple-800'
      case 'hired': return 'bg-emerald-100 text-emerald-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      case 'onhold': return 'bg-yellow-100 text-yellow-800'
      case 'archived': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'applied': return <AlertCircle className="w-4 h-4" />
      case 'shortlisted': return <CheckCircle className="w-4 h-4" />
      case 'screening': return <Clock className="w-4 h-4" />
      case 'interview1': return <Calendar className="w-4 h-4" />
      case 'interview2': return <Calendar className="w-4 h-4" />
      case 'interview3': return <Calendar className="w-4 h-4" />
      case 'hired': return <Star className="w-4 h-4" />
      case 'rejected': return <XCircle className="w-4 h-4" />
      case 'onhold': return <Pause className="w-4 h-4" />
      case 'archived': return <XCircle className="w-4 h-4" />
      default: return <AlertCircle className="w-4 h-4" />
    }
  }

  // Calculate skill score for sorting
  const calculateSkillScore = (candidateSkills: string[] | any, positionSkills: PositionSkill[]): number => {
    if (!candidateSkills || !positionSkills || positionSkills.length === 0) return 0
    
    // Normalize candidate skills
    const normalizedCandidateSkills = Array.isArray(candidateSkills) 
      ? candidateSkills 
      : candidateSkills?.skills 
      ? candidateSkills.skills 
      : []
    
    let score = 0
    let maxScore = 0
    
    positionSkills.forEach(posSkill => {
      const points = posSkill.is_good_to_have ? 1 : 5
      maxScore += points
      
      const hasSkill = normalizedCandidateSkills.some((candSkill: any) => {
        const skillName = typeof candSkill === 'string' 
          ? candSkill.toLowerCase()
          : (candSkill.skillName || candSkill.skill_name || candSkill.name || '').toLowerCase()
        return skillName === posSkill.skill_name.toLowerCase()
      })
      
      if (hasSkill) {
        score += points
      }
    })
    
    return maxScore > 0 ? Math.round((score / maxScore) * 100) : 0
  }

  // Filter and sort candidates
  const filteredAndSortedCandidates = candidates
    .filter(candidate => {
      const matchesSearch = candidate.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           candidate.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           candidate.current_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           candidate.current_company?.toLowerCase().includes(searchTerm.toLowerCase())
      return matchesSearch
    })
    .sort((a, b) => {
      let aValue: any
      let bValue: any
      
      switch (sortBy) {
        case 'name':
          aValue = a.full_name?.toLowerCase() || `${a.first_name} ${a.last_name}`.toLowerCase()
          bValue = b.full_name?.toLowerCase() || `${b.first_name} ${b.last_name}`.toLowerCase()
          break
        case 'score':
          aValue = calculateSkillScore(a.skills, positionSkills)
          bValue = calculateSkillScore(b.skills, positionSkills)
          break
        case 'status':
          aValue = a.application_status || 'active'
          bValue = b.application_status || 'active'
          break
        default:
          return 0
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (candidates.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              {viewConfig.title}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{viewConfig.description}</p>
          </div>
          {viewConfig.showAddButton && onAddCandidate && (
            <button
              onClick={onAddCandidate}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Candidate
            </button>
          )}
        </div>
        
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h4 className="text-sm font-medium text-gray-900 mb-2">{viewConfig.emptyTitle}</h4>
          <p className="text-sm text-gray-600 mb-4">{viewConfig.emptyDescription}</p>
          {viewConfig.showAddButton && onAddCandidate && (
            <button
              onClick={onAddCandidate}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Candidate
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Users className="w-5 h-5 mr-2" />
              {viewConfig.title} ({filteredAndSortedCandidates.length})
            </h3>
            <p className="text-sm text-gray-600 mt-1">{viewConfig.description}</p>
          </div>
          {viewConfig.showAddButton && onAddCandidate && (
            <button
              onClick={onAddCandidate}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Candidate
            </button>
          )}
        </div>

        {/* Search and Sort Controls */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search candidates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
          
          <div className="sm:w-48">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'score' | 'status')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="score">Sort by Skill Score</option>
              <option value="name">Sort by Name</option>
              <option value="status">Sort by Status</option>
            </select>
          </div>
          
          <div className="sm:w-24">
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm flex items-center justify-center hover:bg-gray-50"
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              <ArrowUpDown className="w-4 h-4 mr-1" />
              {sortOrder === 'asc' ? 'Asc' : 'Desc'}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Candidate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Skills & Certifications
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedCandidates.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchTerm ? 'No candidates found' : 'No candidates yet'}
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm 
                      ? 'Try adjusting your search criteria.' 
                      : 'Add candidates to see them in the pipeline.'}
                  </p>
                </td>
              </tr>
            ) : (
              filteredAndSortedCandidates.map((candidate) => {
                const isExpanded = expandedCandidates.has(candidate.id)
                const timeline = candidateTimelines[candidate.id] || []
                const isLoadingTimeline = loadingTimelines.has(candidate.id)
              
                return (
                  <ApplicantTableRow
                    key={candidate.id}
                    applicant={{
                      ...candidate,
                      uuid: candidate.candidate_uuid,
                      candidate_position_uuid: candidate.candidate_position_uuid,
                      application_status: candidate.application_status || 'active',
                      skills: candidate.skills || []
                    }}
                    positionSkills={positionSkills}
                    positionCertifications={positionCertifications}
                    isExpanded={isExpanded}
                    isLoadingTimeline={isLoadingTimeline}
                    timeline={timeline}
                    actingOnCandidate={actingOnCandidate}
                    updating={updating}
                    downloading={downloading}
                    onToggleExpanded={toggleExpanded}
                    // Applicant-specific actions (for viewType === 'applicants')
                    onShortlist={viewConfig.showShortlistActions ? onShortlist : undefined}
                    onUpdateStatus={viewConfig.showShortlistActions ? onUpdateStatus : undefined}
                    onDownloadResume={viewConfig.showShortlistActions ? onDownloadResume : undefined}
                    // Shortlisted candidate actions (for other viewTypes)
                    onCandidateAction={!viewConfig.showShortlistActions ? handleCandidateAction : undefined}
                    onScheduleInterview={!viewConfig.showShortlistActions ? (candidateId: any, candidateName: any, positionId: any) => {
                      window.dispatchEvent(new CustomEvent('openScheduleModal', {
                        detail: {
                          candidateId,
                          candidateName,
                          positionId
                        }
                      }))
                    } : undefined}
                    positionId={positionId}
                  />
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer Stats */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            {searchTerm ? (
              <>
                Showing: <span className="font-medium">{filteredAndSortedCandidates.length}</span> of <span className="font-medium">{candidates.length}</span> candidates
              </>
            ) : (
              <>
                Total Candidates: <span className="font-medium">{candidates.length}</span>
              </>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <span>
              Shortlisted: <span className="font-medium">
                {candidates.filter(c => c.application_status === 'shortlisted').length}
              </span>
            </span>
            <span>
              In Progress: <span className="font-medium">
                {candidates.filter(c => ['screening', 'interview1', 'interview2', 'interview3'].includes(c.application_status || '')).length}
              </span>
            </span>
            <span>
              On Hold: <span className="font-medium">
                {candidates.filter(c => c.application_status === 'onhold').length}
              </span>
            </span>
            <span>
              Rejected: <span className="font-medium">
                {candidates.filter(c => c.application_status === 'rejected').length}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CandidatesTableView